import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MicButton from "./MicButton";
import ListenButton from "./ListenButton";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * ConciergeChat — a lightweight, in-app AI chat surface positioned as
 * an additional wellness resource for members. Streams responses from
 * the backend's `/api/chat/message` endpoint (Server-Sent Events).
 *
 * Compliance is enforced on the backend via the system prompt in
 * `chat_service.py` — this component only renders what the server sends
 * and provides a warm crisis footer.
 */
const SUGGESTED_PROMPTS = [
  "What's the difference between Nervous System Reset and Peak State?",
  "How long is a session?",
  "Is this a good fit if I've never done breathwork?",
  "What happens after I fill out the check-in?",
];

const GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "Hi — I'm the Integrated Wellness Concierge. I can explain the programs, help you decide where to start, or answer quick wellness questions. What's on your mind?",
};

let _msgCounter = 0;
const nextMsgId = () => `msg-${Date.now().toString(36)}-${++_msgCounter}`;

function newSessionId() {
  const rand =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return "concierge-" + rand;
}

export default function ConciergeChat() {
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [research, setResearch] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const canSend = input.trim().length > 0 && !busy;

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;
      setError(null);
      setInput("");
      setMessages((m) => [
        ...m,
        { id: nextMsgId(), role: "user", content: trimmed },
        { id: nextMsgId(), role: "assistant", content: "", streaming: true },
      ]);
      setBusy(true);

      // Research mode: Perplexity Sonar, non-streaming, cited sources.
      if (research) {
        try {
          const res = await fetch(`${API}/chat/research`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId, message: trimmed }),
          });
          if (!res.ok) {
            const detail = (await res.json().catch(() => ({})))?.detail;
            throw new Error(typeof detail === "string" ? detail : "Research failed");
          }
          const data = await res.json();
          setMessages((m) => {
            const next = m.slice();
            next[next.length - 1] = {
              ...next[next.length - 1],
              content: data.answer,
              citations: data.citations || [],
              streaming: false,
            };
            return next;
          });
        } catch (err) {
          console.error("Research request failed", err);
          setMessages((m) => m.slice(0, -1));
          setError(err.message || "Research is unavailable right now — please try again.");
        } finally {
          setBusy(false);
        }
        return;
      }

      let assistantIndex;
      setMessages((m) => {
        assistantIndex = m.length - 1;
        return m;
      });

      try {
        const res = await fetch(`${API}/chat/message`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: trimmed }),
        });
        if (!res.ok || !res.body) {
          throw new Error(`HTTP ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let assistantText = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";
          for (const chunk of events) {
            const line = chunk.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            let payload;
            try {
              payload = JSON.parse(line.slice(6));
            } catch {
              continue;
            }
            if (payload.delta) {
              assistantText += payload.delta;
              const snapshot = assistantText;
              setMessages((m) => {
                const next = m.slice();
                next[next.length - 1] = {
                  ...next[next.length - 1],
                  content: snapshot,
                  streaming: true,
                };
                return next;
              });
            } else if (payload.done) {
              setMessages((m) => {
                const next = m.slice();
                next[next.length - 1] = {
                  ...next[next.length - 1],
                  content: assistantText,
                  streaming: false,
                };
                return next;
              });
            } else if (payload.error) {
              throw new Error(payload.error);
            }
          }
        }
      } catch (err) {
        setError(
          "The concierge couldn't respond just now — please try again in a moment."
        );
        setMessages((m) => m.slice(0, -1)); // drop the empty assistant placeholder
      } finally {
        setBusy(false);
      }
    },
    [busy, sessionId, research]
  );

  const suggested = useMemo(
    () => (messages.length === 1 ? SUGGESTED_PROMPTS : []),
    [messages.length]
  );

  return (
    <section
      id="concierge-chat"
      className="mt-20 fade-up"
      data-testid="concierge-chat-section"
    >
      <div className="iw-section-head">
        <div className="iw-section-head__label">
          § Additional Resource — AI Concierge
        </div>
        <h2 className="iw-section-head__title">
          Have a quick question? Ask the concierge.
        </h2>
        <p
          className="text-base sm:text-lg max-w-3xl"
          style={{ color: "var(--iw-body)" }}
        >
          A member-facing AI assistant for explaining the programs, helping you
          decide where to start, and answering quick wellness questions.
          Strictly non-medical — anything requiring clinical judgment is routed
          to a human.
        </p>
      </div>

      <div
        className="iw-card overflow-hidden"
        style={{ borderTop: "3px solid var(--iw-accent-red)" }}
      >
        <div
          ref={scrollRef}
          className="px-5 sm:px-7 py-5 overflow-y-auto"
          style={{ maxHeight: 460, minHeight: 300, background: "var(--iw-surface)" }}
          data-testid="concierge-messages"
        >
          {messages.map((m) => (
            <Bubble key={m.id} role={m.role} content={m.content} streaming={m.streaming} citations={m.citations} />
          ))}
          {busy && messages[messages.length - 1]?.content === "" && <TypingDots />}
        </div>

        {suggested.length > 0 && (
          <div
            className="px-5 sm:px-7 py-3 flex flex-wrap gap-2"
            style={{ borderTop: "1px solid var(--iw-border)", background: "var(--iw-blue-tint)" }}
            data-testid="concierge-suggestions"
          >
            {suggested.map((p, i) => (
              <button
                key={p}
                type="button"
                onClick={() => sendMessage(p)}
                disabled={busy}
                className="iw-mono uppercase text-left px-3 py-2 transition-colors"
                style={{
                  border: "1px solid var(--iw-border)",
                  borderRadius: "2px",
                  background: "var(--iw-surface)",
                  color: "var(--iw-primary)",
                  fontSize: "10px",
                  letterSpacing: "0.16em",
                  fontWeight: 600,
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                data-testid={`concierge-suggestion-${i}`}
              >
                {p} ↗
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(input);
          }}
          className="px-5 sm:px-7 py-4 flex items-end gap-3"
          style={{ borderTop: "1px solid var(--iw-border)" }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
            placeholder="Ask about the programs, technique fit, session length…"
            rows={1}
            maxLength={2000}
            disabled={busy}
            className="flex-1 resize-none px-3 py-2 text-base leading-relaxed"
            style={{
              border: "1px solid var(--iw-border)",
              borderRadius: "2px",
              background: "#fff",
              color: "var(--iw-primary)",
              fontFamily: "'IBM Plex Sans', sans-serif",
              outline: "none",
            }}
            data-testid="concierge-input"
          />
          <MicButton
            onTranscript={(text) => setInput((prev) => (prev ? `${prev.trim()} ${text}` : text))}
            disabled={busy}
            testId="concierge-mic-btn"
          />
          <button
            type="button"
            onClick={() => setResearch((v) => !v)}
            className="inline-flex items-center iw-mono uppercase transition-all flex-shrink-0"
            style={{
              padding: "0 12px",
              height: 40,
              fontSize: "10px",
              letterSpacing: "0.14em",
              fontWeight: 700,
              borderRadius: "2px",
              border: `1px solid ${research ? "var(--iw-blue)" : "var(--iw-border)"}`,
              background: research ? "var(--iw-blue)" : "#fff",
              color: research ? "#fff" : "var(--iw-muted)",
              cursor: "pointer",
            }}
            title={research ? "Research mode on — web-grounded answers with sources" : "Turn on research mode for cited, evidence-backed answers"}
            aria-pressed={research}
            data-testid="concierge-research-toggle"
          >
            Research{research ? " · ON" : ""}
          </button>
          <button
            type="submit"
            disabled={!canSend}
            className="iw-btn-primary"
            style={{
              opacity: canSend ? 1 : 0.4,
              cursor: canSend ? "pointer" : "not-allowed",
            }}
            data-testid="concierge-send-btn"
          >
            Send <span aria-hidden="true">↗</span>
          </button>
        </form>

        {error && (
          <div
            className="px-5 sm:px-7 py-2 iw-mono uppercase"
            style={{
              background: "var(--iw-blue-tint)",
              color: "var(--iw-accent-red)",
              fontSize: "10px",
              letterSpacing: "0.20em",
              fontWeight: 600,
              borderTop: "1px solid var(--iw-border)",
            }}
            data-testid="concierge-error"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>

      <div
        className="mt-4 text-xs sm:text-sm leading-relaxed"
        style={{ color: "var(--iw-muted)" }}
        data-testid="concierge-disclaimer"
      >
        <strong style={{ color: "var(--iw-primary)" }}>Non-medical.</strong>{" "}
        The concierge is a wellness assistant, not a clinician. If you&apos;re in
        crisis or thinking about harming yourself, call or text{" "}
        <strong>988</strong> or dial <strong>911</strong>.
      </div>
    </section>
  );
}

function Bubble({ role, content, streaming, citations }) {
  const isUser = role === "user";
  return (
    <div
      className="mb-4 flex"
      style={{ justifyContent: isUser ? "flex-end" : "flex-start" }}
      data-testid={`concierge-msg-${role}`}
    >
      <div
        className="max-w-[80%] px-4 py-3 leading-relaxed text-[15px]"
        style={{
          borderRadius: "2px",
          border: `1px solid ${isUser ? "var(--iw-primary)" : "var(--iw-border)"}`,
          background: isUser ? "var(--iw-primary)" : "#fff",
          color: isUser ? "#fff" : "var(--iw-primary)",
          whiteSpace: "pre-wrap",
        }}
      >
        {content}
        {!isUser && citations && citations.length > 0 && (
          <div className="mt-2.5 pt-2 border-t" style={{ borderColor: "var(--iw-border-soft)" }} data-testid="research-sources">
            <span className="iw-mono uppercase block mb-1" style={{ color: "var(--iw-muted)", fontSize: "9px", letterSpacing: "0.16em", fontWeight: 600 }}>
              Sources
            </span>
            {citations.slice(0, 5).map((url, idx) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block truncate text-xs mb-0.5"
                style={{ color: "var(--iw-blue)" }}
                data-testid={`research-source-${idx}`}
              >
                {idx + 1}. {url.replace(/^https?:\/\/(www\.)?/, "")}
              </a>
            ))}
          </div>
        )}
        {!isUser && !streaming && content && (
          <div className="mt-2">
            <ListenButton text={content} size={26} testId="concierge-listen-btn" />
          </div>
        )}
        {streaming && (
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 8,
              height: 14,
              marginLeft: 3,
              verticalAlign: "-2px",
              background: "var(--iw-accent-red)",
              animation: "peakKenBurns 0.9s ease-in-out infinite",
            }}
          />
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="mb-4 flex" data-testid="concierge-typing">
      <div
        className="px-4 py-3 iw-mono uppercase"
        style={{
          borderRadius: "2px",
          border: "1px solid var(--iw-border)",
          background: "#fff",
          color: "var(--iw-muted)",
          fontSize: "10px",
          letterSpacing: "0.20em",
          fontWeight: 600,
        }}
      >
        Thinking · · ·
      </div>
    </div>
  );
}
